/**
 * Server functions and utilities for the Portals app.
 *
 * tRPC-backed queries and mutations are now accessed via the typed `trpc`
 * proxy from `@/lib/trpc`:
 *
 *   import { trpc } from '@/lib/trpc';
 *   trpc.portal.getAuthor.queryOptions({ portalId, locale })
 *   trpc.payments.preregisterDonation.mutationOptions()
 *
 * This file retains:
 * - Direct server functions (not suitable for tRPC: streaming, etc.)
 * - Utility functions (ensureSeoData, extractErrorInfo, currentWindowKey)
 */

// ── Imports ──────────────────────────────────────────────────────────────────

import { createServerFn } from '@tanstack/react-start';
import { isServer } from '@tanstack/react-query';
import { parseRequest } from './server-functions-shared.server';
import { eventEmitter } from './server-broadcast.server';
import { clientTelemetryContext, telemetryCaller } from './wide-event.server';
import { allowTelemetryActions, allowTelemetryErrorReport } from './telemetry-rate-limit.server';
import {
	type ClientAction,
	type ClientErrorBreadcrumb,
	PORTAL_FRONTEND_ERROR_MARKER,
	recordClientActions,
	recordClientError,
	reportEventError,
	reportRawError,
} from '@worldcoo/monolith/wide-events';
import * as z from 'zod';
import { shouldReportFrontendError } from './frontend-error-filter';
import { portalLocalesExtended } from '@worldcoo/monolith/portals';

// ── Types ────────────────────────────────────────────────────────────────────

export type PutProjectErrorCode = 'PROJECT_TOO_LARGE' | 'SAVE_PROJECT_FAILED';
export type TranslateErrorCode = 'UNAUTHORIZED' | 'CAPTCHA_FAILED' | 'TRANSLATION_FAILED';
export type ContractErrorCode = 'NETWORK_ERROR' | 'CAPTCHA_FAILED' | 'CONTRACT_GENERATION_FAILED';
export type DraftContractErrorCode = 'DRAFT_NOT_FOUND' | 'CONTRACT_GENERATION_FAILED';
export type OnboardingDraftErrorCode = 'NETWORK_ERROR' | 'SAVE_FAILED';
export type LiveCounterMode = 'day' | 'realtime';

// ── Shared helpers ────────────────────────────────────────────────────────────

export async function ensureSeoData(context: { isBot: boolean }, promise: Promise<unknown>): Promise<void> {
	if (isServer && context.isBot) {
		await promise;
	}
}

const WINDOW_MS = 5 * 60_000;

/** Returns the current 5-minute window key (e.g. "2026-02-13T14:10") */
export function currentWindowKey() {
	const windowStart = Math.floor(Date.now() / WINDOW_MS) * WINDOW_MS;
	return new Date(windowStart).toISOString().slice(0, 16);
}

// ── Server functions that are called directly (not through tRPC) ─────────────

export const getRequestContext = createServerFn()
	.validator(z.enum(portalLocalesExtended).optional())
	.handler(({ data }) => parseRequest(data));

/**
 * A user action leading up to a crash. Bounded and sanitized by the client before it is sent; the
 * `satisfies` is what ties this wire schema to the shape the recorder expects, so adding a field to one
 * without the other stops compiling.
 */
const breadcrumbSchema = z.object({
	t: z.number(),
	type: z.string().max(32),
	message: z.string().max(200),
}) satisfies z.ZodType<ClientErrorBreadcrumb>;

const clientActionSchema = z.object({
	t: z.number(),
	type: z.enum(['nav', 'click']),
	label: z.string().max(120),
	path: z.string().max(300),
}) satisfies z.ZodType<ClientAction>;

/**
 * Every string is length-bounded even though the browser half already truncates: this endpoint is
 * unauthenticated and reachable by anything that can POST, and its payload lands in a stderr line that
 * CloudWatch cuts at 256KB — an unbounded `message` or `stack` is a broken ticket. `catch` keeps a
 * malformed field from failing the whole report; `max` is enforced by truncating rather than rejecting,
 * for the same reason.
 */
const boundedErrorText = (max: number, fallback: string) =>
	z
		.string()
		.catch(fallback)
		.transform((value) => value.slice(0, max));

export const logErrorFn = createServerFn({ method: 'POST' })
	.validator(
		z.object({
			route: boundedErrorText(300, 'unknown'),
			name: boundedErrorText(200, 'UnknownError'),
			message: boundedErrorText(2000, '(no message)'),
			// Truncated, not `.max()`-rejected: a rejection fails the whole report, losing a real crash.
			stack: z
				.string()
				.transform((value) => value.slice(0, 20_000))
				.optional(),
			// `recoverable` is the client entry's `onRecoverableError` (see `src/client.tsx`): a fault React
			// recovered from on its own, above all a hydration mismatch. Note this enum has no `.catch()`,
			// so an unlisted value fails validation and loses the whole report — add the member here before
			// sending a new one.
			source: z.enum(['boundary', 'global', 'unhandledrejection', 'recoverable']).optional(),
			breadcrumbs: z.array(breadcrumbSchema).max(20).optional(),
		}),
	)
	.handler(({ data }) => {
		// Bounded per caller as well as per call. One report costs a queue message AND a ticketable stderr
		// line whose fingerprint the caller controls through the message, so an open endpoint with no
		// ceiling is an open supply of distinct Jira issues. Dropped silently: a failing telemetry call
		// would surface as another browser console error, which is the signal we are here to collect.
		//
		// Gated on the CHEAP caller lookup, before the full context is resolved, so a script hammering this
		// endpoint doesn't make us do the cookie and HMAC work its request will never use.
		if (!allowTelemetryErrorReport(telemetryCaller())) {
			return { success: true, logged: false, ticketed: false };
		}

		// Record the crash as a `client_error` wide event, and print that same event below. The event is
		// strictly richer than the ad-hoc context this used to assemble by hand — it adds the release the
		// browser was running, the locale/viewport/platform, the NGO session, the breadcrumb trail and the
		// trace id of the render that produced the page — so the ticket and the analytics row now describe
		// the crash identically instead of diverging.
		const event = recordClientError({ ...clientTelemetryContext(), ...data });

		// Server-side backstop for the noise filter the browser already applies before calling us.
		//
		// `shouldReportFrontendError` lives in the CLIENT bundle, so it only protects us for browsers
		// running a build that HAS it. A long-lived mobile tab keeps its bundle across our deploys, and the
		// injected-script junk of TEC-3648/3649/3650 kept arriving for a full day after the filter shipped
		// — every burst from a browser whose JS predated it (confirmed in the pro logs: the reports carry
		// none of the telemetry headers added the same morning, so those clients were provably stale).
		// A client-side-only filter can never close that window, because the code that would drop the
		// report is the code the stale client does not have.
		//
		// Applied to the TICKET only. The `client_error` event is already buffered above and stays that
		// way: `error = true` is still the right flag for an error-rate view, and dropping the analytics
		// row would hide how much of this traffic there is. Same split as `CLIENT_FAULT_RPC_CODES` in the
		// wide-event reporter — in the event, out of the ticket board.
		if (!shouldReportFrontendError(data.name, data.message, data.stack)) {
			return { success: true, logged: true, ticketed: false };
		}

		// Exactly ONE line may contain the marker: the parser tickets every occurrence of it, and a previous
		// structured-context line carrying a `type: 'PORTAL_FRONTEND_ERROR'` field was matched as a SECOND,
		// malformed ticket. Both branches go through `report-line.ts`, which owns the one-line rule.
		if (event) {
			reportEventError(event, { marker: PORTAL_FRONTEND_ERROR_MARKER });
		} else {
			// Telemetry disabled or the event failed to build — still ticket the crash, just thinly.
			reportRawError(PORTAL_FRONTEND_ERROR_MARKER, `${data.name} — ${data.message}`);
		}
		return { success: true, logged: true, ticketed: true };
	});

/**
 * Ingest for the session timeline. The browser batches lightweight nav/click actions and posts them
 * here; each becomes a `client_action` wide event. See `lib/session-actions.ts` for what the client
 * is (and is not) allowed to collect.
 *
 * Bounded at the boundary twice over, because every accepted action becomes its own queue message and
 * durable row. The validator caps ONE call (50 actions, every string length-limited); the rate limit
 * caps the CALLER across calls, which is what stops a script pointed at this open endpoint from
 * turning it into an amplifier. Excess is dropped silently rather than rejected: a failing telemetry
 * call would surface as a browser console error and pollute the signal we are here to collect.
 */
export const recordClientActionsFn = createServerFn({ method: 'POST' })
	.validator(z.object({ actions: z.array(clientActionSchema).max(50) }))
	.handler(({ data }) => {
		// Cheap gate first; the full context is only worth resolving for actions we are going to keep.
		const allowed = allowTelemetryActions(telemetryCaller(), data.actions.length);
		if (allowed > 0) {
			recordClientActions({
				...clientTelemetryContext(),
				actions: data.actions.slice(0, allowed),
				nowMs: Date.now(),
			});
		}
		return { success: true } as const;
	});

// ── Streaming server functions ───────────────────────────────────────────────

export const waitForConfirmedDonationFn = createServerFn()
	.validator(z.string())
	.handler(async function* ({ data: donationIdToConfirm }) {
		const promise = new Promise<'donation.confirmed' | 'donation.timeout'>((resolve) => {
			const onConfirmed = (donationId: string) => {
				if (donationId === donationIdToConfirm) {
					eventEmitter.off('donation.confirmed', onConfirmed);
					resolve('donation.confirmed');
				}
			};
			eventEmitter.on('donation.confirmed', onConfirmed);
			setTimeout(() => {
				eventEmitter.off('donation.confirmed', onConfirmed);
				resolve('donation.timeout');
			}, 10_000);
		});
		yield await promise;
	});

// ── Utility functions ────────────────────────────────────────────────────────

export function extractErrorInfo(error: unknown): {
	name: string;
	message: string;
	stack: string | undefined;
} {
	if (error instanceof Error) {
		return { name: error.name || 'Error', message: error.message || '(empty message)', stack: error.stack };
	}
	if (typeof error === 'string') {
		return { name: 'StringError', message: error, stack: undefined };
	}
	if (error === null || error === undefined) {
		return { name: 'NullishError', message: `Thrown value was ${String(error)}`, stack: undefined };
	}
	if (typeof error === 'object') {
		const name = 'name' in error && typeof error.name === 'string' ? error.name : 'ObjectError';
		const message =
			'message' in error && typeof error.message === 'string'
				? error.message
				: 'error' in error && typeof error.error === 'string'
					? error.error
					: safeStringify(error);
		const stack = 'stack' in error && typeof error.stack === 'string' ? error.stack : undefined;
		return { name, message, stack };
	}
	return { name: 'UnknownError', message: safeStringify(error), stack: undefined };
}

function safeStringify(value: unknown): string {
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
}

// `shouldReportFrontendError` and the rest of the reporting predicates live in
// ./frontend-error-filter — a dependency-free module so they can be unit-tested without pulling in
// the server-function imports above. Import them from there directly, not through this module.
